import { parse, format } from 'date-fns';
import { RouteLocation } from 'vue-router';
import { Currency, Lang } from 'types';
import i18n from '../../plugins/i18n';
import { of } from 'rxjs';

export namespace Cars {
  export class SearchRequestBody {
    name: string = 'search-car';
    payload: ISearchCriteria;

    constructor(requestBody: ISearchCriteria) {
      this.payload = requestBody;
    }
  }

  export interface ISearchCriteria {
    test: boolean;
    pick_up_location: string;
    drop_off_location: string;
    pick_up_date: string;
    drop_off_date: string;
    pick_up_time: string;
    drop_off_time: string;
    currency?: string;
    country?: string;
    radius?: number;
    sources?: string;
    age?: number;
  }

  export class SearchCriteria {
    test: boolean;
    pickUpId?: string;
    pickUpLocation?: string;
    pickUpDate: Date;
    pickUpTime: string;
    dropOffId?: string;
    dropOffLocation?: string;
    dropOffDate: Date;
    dropOffTime: string;
    currency?: string;
    country?: string;
    radius?: number;
    sources?: string;
    age?: number;
    offerId?: string;

    constructor({
      pickUpId,
      pickUpLocation,
      dropOffId,
      dropOffLocation,
      pickUpDate,
      dropOffDate,
      pickUpTime,
      dropOffTime,
      currency,
      country,
      radius,
      sources,
      offerId,
      age,
    }: {
      pickUpId?: string;
      pickUpLocation?: string;
      dropOffId?: string;
      dropOffLocation?: string;
      pickUpDate: Date;
      dropOffDate: Date;
      pickUpTime: string;
      dropOffTime: string;
      currency?: string;
      country?: string;
      radius?: number;
      sources?: string;
      offerId?: string;
      age?: number;
    }) {
      this.test = false;
      this.pickUpId = pickUpId;
      this.pickUpLocation = pickUpLocation;
      this.dropOffId = dropOffId;
      this.dropOffLocation = dropOffLocation;
      this.pickUpDate = pickUpDate;
      this.dropOffDate = dropOffDate;
      this.pickUpTime = pickUpTime;
      this.dropOffTime = dropOffTime;
      this.currency = currency;
      this.country = country;
      this.radius = radius;
      this.sources = sources;
      this.age = age;
      this.offerId = offerId;
    }

    static empty() {
      const today = new Date();
      const pickUpDate = new Date(today.getTime() + 24 * 60 * 60 * 1000);
      const dropOffDate = new Date(today.getTime() + 48 * 60 * 60 * 1000);
      return new SearchCriteria({
        pickUpDate: pickUpDate,
        dropOffDate: dropOffDate,
        pickUpTime: '12:00',
        dropOffTime: '12:00',
      });
    }

    toRequestBody(currency: Currency.CURRENCIES): ISearchCriteria {
      const yyyyMMdd = 'YYYYMMDD';

      return {
        test: this.test,
        pick_up_location: this.pickUpId,
        currency: currency,
        drop_off_location: this.dropOffId,
        pick_up_date: format(this.pickUpDate, yyyyMMdd),
        drop_off_date: format(this.dropOffDate, yyyyMMdd),
        pick_up_time: (this.pickUpTime ?? '').replace(':', ''),
        drop_off_time: (this.dropOffTime ?? '').replace(':', ''),
      };
    }

    toQuery(offerId?: string) {
      const yyyyMMdd = 'YYYYMMDD';
      const query = {
        pid: this.pickUpId,
        pn: this.pickUpLocation,
        pd: format(this.pickUpDate, yyyyMMdd),
        pt: this.pickUpTime,
        did: this.dropOffId ? this.dropOffId : this.pickUpId,
        dn: this.dropOffLocation ? this.dropOffLocation : this.pickUpLocation,
        dd: format(this.dropOffDate, yyyyMMdd),
        dt: this.dropOffTime,
      };
      if (offerId) {
        query['of'] = offerId;
      }
      return query;
    }

    toAffilateUrlParams() {
      const [puHour, puMinute] = this.pickUpTime.split(':').map(Number);
      const [doHour, doMinute] = this.dropOffTime.split(':').map(Number);

      const query = {
        puHour,
        puMinute,
        puDay: this.pickUpDate.getDate(),
        puMonth: this.pickUpDate.getMonth() + 1,
        puYear: this.pickUpDate.getFullYear(),

        doHour,
        doMinute,
        doDay: this.dropOffDate.getDate(),
        doMonth: this.dropOffDate.getMonth() + 1,
        doYear: this.dropOffDate.getFullYear(),

        from: this.pickUpId,
        to: this.dropOffId,
        locationName: this.pickUpLocation,
        dropLocationName: this.dropOffLocation,
        location: this.pickUpId,
        dropLocation: this.dropOffId,
      };
      return Object.keys(query)
        .map(key => key + '=' + query[key])
        .join('&');
    }

    isPickUpValid(): boolean {
      return this.pickUpId && this.pickUpLocation ? true : false;
    }

    isDropOffValid(): boolean {
      return this.dropOffId && this.dropOffLocation ? true : false;
    }

    getPickUpDetails(): string {
      const ddMMM = 'D MMM';
      const date = this.pickUpTime + ', ' + format(this.pickUpDate, ddMMM);
      return this.pickUpLocation ? this.pickUpLocation + ' - ' + date : date;
    }

    getDropOffDetails(): string {
      const ddMMM = 'D MMM';
      const date = this.dropOffTime + ', ' + format(this.dropOffDate, ddMMM);
      return this.dropOffLocation ? this.dropOffLocation + ' - ' + date : date;
    }

    mobileHeaderTitle(): string {
      let arr = [this.pickUpLocation];
      if (this.dropOffLocation && this.dropOffLocation != this.pickUpLocation) {
        arr.push(this.dropOffLocation);
      }
      return arr.join(' - ');
    }

    mobileHeaderSubtitle(): String {
      const ddMMM = 'D MMM';
      return (
        this.pickUpTime +
        ', ' +
        format(this.pickUpDate, ddMMM) +
        ' - ' +
        this.dropOffTime +
        ', ' +
        format(this.dropOffDate, ddMMM)
      );
    }

    static parseRoute(route: RouteLocation): SearchCriteria {
      if (
        !route.query.pid ||
        !route.query.pn ||
        !route.query.pd ||
        !route.query.pt ||
        !route.query.did ||
        !route.query.dn ||
        !route.query.dd ||
        !route.query.dt
      ) {
        return SearchCriteria.empty();
      }

      const pickUpId = route.query.pid as string;
      const pickUpName = route.query.pn as string;
      const pickUpDate = route.query.pd as string;
      const pickUpTime = route.query.pt as string;
      const dropOffId = route.query.did as string;
      const dropOffName = route.query.dn as string;
      const dropOffDate = route.query.dd as string;
      const dropOffTime = route.query.dt as string;
      const offerId = route.query.of as string;

      return new SearchCriteria({
        pickUpId: pickUpId,
        pickUpLocation: pickUpName,
        pickUpDate: parse(pickUpDate),
        pickUpTime: pickUpTime,
        dropOffId: dropOffId,
        dropOffLocation: dropOffName,
        dropOffDate: parse(dropOffDate),
        dropOffTime: dropOffTime,
        offerId: offerId,
      });
    }
  }

  export interface OfferDetailsResponse {
    offer: Offer;
    dropoff: Station;
    pickup: Station;
    pickup_date: string;
    return_date: string;
    vendor: Vendor;
  }

  export interface InitAsyncSearchResponse {
    status: string;
    job_id: string;
    message: string;
  }

  export interface AsyncSearchResponse {
    status: string;
    job_status: string;
    result: SearchResults;
  }

  export interface SearchResults {
    error?: string;
    request_id: string;
    expires_at: string;
    pick_up_date: string;
    pick_up_time: string;
    drop_off_date: string;
    drop_off_time: string;
    currency: string;
    country: string;
    test: boolean;
    pick_up_location: Location;
    drop_off_location: Location;
    offers: Offer[];
    stations: Station[];
    vendors: Vendor[];
    cancellation_policy: CancellationPolicy;
    collections: Collection[];
  }

  export interface Collection {
    name: string;
    image: string;
    group_by?: string;
    refs: string[];
    background_color?: string;
  }

  export interface Offer {
    offer_key: string;
    car: Car;
    extras: string[];
    term_conditions: string;
    pickup_station_id: string;
    dropoff_station_id: string;
    fuel_policy: FuelPolicy;
    unlimited_mileage: boolean;
    vendor: FakeVendor;
    price: Price;
    cancellation_policy: string;
    free_cancellation: boolean;
    card_required: boolean;
    no_show_fee: NoShowFee;
    cancellation_fee: CancellationFee[];
    price_details: PriceDetail[];
    payment_gate: string;
    full_source: string;
    name: string;
    source: string;
    source_tag: string;
    excesses_info: ExcessesInfo[];
    insurance_info?: string;

    test: boolean;
    is_source_public: boolean;
    rate_type: string;
    best_deal: boolean;
    collections: string[];
  }

  export enum ExcessesInfoType {
    theft = 'theft',
    damage = 'damage',
    security = 'security',
  }

  export interface ExcessesInfo {
    type: ExcessesInfoType;
    currency: string;
    currency_original: string;
    amount: number;
  }

  export interface Car {
    acriss: string;
    make_model: string;
    transmission: string;
    air_condition: string;
    fuel_type: string;
    category: string;
    collection: string;
    group: string;
    images: string[];
    make_models: MakeModel;
    image_small: string;
    image_large: string;
    passengers: string;
    baggage_seat: string;
    door_count: string;
    alias: string;
    or_similar: boolean;
  }

  export interface FuelPolicy {
    id: number;
    name: string;
    description: string;
  }

  export interface MakeModel {
    make: string;
    model: string;
  }

  export interface Location {
    id: string;
    name: string;
    country_code: string;
    lat: number;
    lon: number;
    type: string;
  }

  export interface Price {
    total: number;
    daily: number;
    currency: string;
    deposit: Deposit;
    remainder: Deposit;
    cashback: number;
    total_with_cashback: number;
    savings: number;
    public_lowest: number;
    public_lowest_daily: number;
    public_sources: PricePublicSource[];
  }

  export interface PriceDetail {
    name: string;
    amount?: number;
    currency?: string;
    included?: Boolean;
  }

  export interface Deposit {
    amount: number;
    currency: string;
    amount_original: number;
    currency_original: number;
  }

  export interface PricePublicSource {
    price: number;
    source: string;
    source_name: string;
  }

  export interface FakeVendor {
    code: number;
    name: string;
    image?: string;
    color: string;
  }

  export interface Vendor {
    code: number;
    source_code?: string;
    name: string;
    image?: string;
    image_url?: string;
    image_our?: string;
    color?: string;
    vendor_url?: string;
    ratings?: VendorRating;
    cancellation_policy?: CancellationPolicy;
    rate_types?: RateTypes;
  }

  export interface VendorRating {
    average: number;
  }

  export interface RateTypes {
    postpaid: RateType;
    partpaid: RateType;
    prepaid: RateType;
  }

  export interface RateType {
    text: string;
    subtext: string;
  }

  export interface NoShowFee {
    amount: number;
    currency: string;
  }

  export interface CancellationFee {
    amount: number;
    currency: string;
    date_before: string;
  }

  export interface CancellationPolicy {
    AM1?: string;
    CT1?: string;
    RC1?: string;
  }

  export interface Station {
    id: number;
    name: string;
    description: string;
    address: string;
    in_airport: boolean;
    location: string;
    latitude: number;
    longitude: number;
    distance: string;
    iata: string;
    station_id: string;
    place_details: StationPlaceDetails;
  }

  export interface StationPlaceDetails {
    rating?: number;
  }

  export enum DefaultFilter {
    Any = 'Any',
  }

  export enum PayType {
    PayLater = 'postpaid',
    PrePay = 'prepaid',
    PartPaid = 'partpaid',
  }

  export enum OptionsKeys {
    CarType = 'Car Type',
    Rating = 'Rating',
    PriceRange = 'Price Range',
    FreeCancellation = 'Free Cancellation',
    PayLater = 'Pay Later',
    Supplier = 'Supplier',
    Seats = 'Seats',
    GearType = 'Gear type',
    FuelType = 'Fuel type',
  }

  export class FilterOptions {
    priceRange: PriceRange;
    type: CarType[];
    rating: Rating[];
    freeCancellation?: Options;
    postPay?: Options;
    seats: Options[];
    vendors: Options[];
    transmissions: Options[];
    fuelType: Options[];

    constructor({
      priceRange,
      type,
      rating,
      freeCancellation,
      postPay,
      seats,
      vendors,
      transmissions,
      fuelType,
    }: {
      priceRange: PriceRange;
      type: CarType[];
      rating: Rating[];
      freeCancellation?: Options;
      postPay?: Options;
      seats: Options[];
      vendors: Options[];
      transmissions: Options[];
      fuelType: Options[];
    }) {
      this.priceRange = priceRange;
      this.type = type;
      this.freeCancellation = freeCancellation;
      this.rating = rating;
      this.postPay = postPay;
      this.seats = seats;
      this.vendors = vendors;
      this.transmissions = transmissions;
      this.fuelType = fuelType;
    }

    static compose(offers: CarDetails[]): FilterOptions {
      const priceRange = PriceRange.compose(offers);
      const rating = Rating.compose(offers);
      const type = CarType.compose(offers);
      const vendors: Options[] = offers
        .map(it => it.vendorName)
        .distinct()
        .map(it => new Options(it, OptionsKeys.Supplier));
      const freeCancellation = offers.some(it => it.freeCancellation);
      const payLater = offers.some(
        it => it.source.rate_type == PayType.PayLater,
      );

      const seats: Options[] = offers
        .map(it => it.passangers)
        .distinct()
        .map(it => new Options(it, OptionsKeys.Seats));

      const transmissions: Options[] = offers
        .map(it => it.transmission)
        .distinct()
        .map(it => new Options(it, OptionsKeys.GearType));

      const fuelType: Options[] = offers
        .map(it => it.fuelType)
        .distinct()
        .map(it => new Options(it, OptionsKeys.FuelType));

      return new FilterOptions({
        priceRange,
        type,
        rating,
        freeCancellation: freeCancellation
          ? new Options(
              String(i18n.global.t('filters.freeCancellation')),
              OptionsKeys.FreeCancellation,
            )
          : null,
        postPay: payLater
          ? new Options(OptionsKeys.PayLater, OptionsKeys.PayLater)
          : null,
        seats,
        transmissions,
        vendors,
        fuelType,
      });
    }

    filterOnDefaults(): boolean {
      return (
        this.rating.nothingSelected() ||
        (this.rating.find(it => it.type == RatingType.Any).selected &&
          this.type.nothingSelected()) ||
        (this.type.find(it => it.type == DefaultFilter.Any).selected &&
          ((this.freeCancellation && !this.freeCancellation.selected) ||
            true) &&
          ((this.postPay && !this.postPay.selected) || true) &&
          this.vendors.nothingSelected() &&
          this.fuelType.nothingSelected() &&
          this.seats.nothingSelected() &&
          this.transmissions.nothingSelected() &&
          this.priceRange.right === this.priceRange.max &&
          this.priceRange.left === this.priceRange.min)
      );
    }
  }

  export class Selectable {
    selected: boolean = false;
    disabled?: boolean;
    constructor(selected: boolean) {
      this.selected = selected;
    }
    toggle() {
      this.selected = !this.selected;
    }
  }

  export class Options extends Selectable {
    name: string;
    key: OptionsKeys;

    constructor(name: string, key: OptionsKeys) {
      super(false);
      this.name = name;
      this.key = key;
    }
  }

  export class CarType extends Selectable {
    type: string;
    enabled: boolean = true;

    constructor(type: string, enabled: boolean) {
      super(type == DefaultFilter.Any ? true : false);
      this.type = type;
      this.enabled = enabled;
    }

    matches(car: CarDetails) {
      if (this.type == DefaultFilter.Any) {
        return car;
      }
      return this.type == car.category;
    }

    static compose(offers: CarDetails[]): CarType[] {
      const categories: string[] = offers
        .map(offer => offer.category)
        .distinct();
      const types: CarType[] = [];
      types.push(new CarType(DefaultFilter.Any, true));
      categories.forEach(it => {
        types.push(new CarType(it, true));
      });
      return types;
    }
  }

  export enum RatingType {
    Any = 'Any',
    ThreeHalf = '3.5+ Good',
    Four = '4.0+ Very good',
    FourHalf = '4.5+ Exceptional',
  }

  export class Rating extends Selectable {
    type: RatingType;
    enabled: boolean;

    constructor(type: RatingType, enabled: boolean) {
      super(type === RatingType.Any ? true : false);
      this.type = type;
      this.enabled = enabled;
    }

    matches(offer: CarDetails): boolean {
      switch (this.type) {
        case RatingType.Any:
          return true;
        case RatingType.ThreeHalf:
          return offer.vendorRating >= 35;
        case RatingType.Four:
          return offer.vendorRating >= 40;
        case RatingType.FourHalf:
          return offer.vendorRating >= 45;
        default:
          return false;
      }
    }

    static compose(hotels: CarDetails[]): Rating[] {
      return [
        new Rating(RatingType.Any, true),
        new Rating(
          RatingType.ThreeHalf,
          hotels.some(it => it.vendorRating >= 30 && it.vendorRating < 40),
        ),
        new Rating(
          RatingType.Four,
          hotels.some(it => it.vendorRating >= 40 && it.vendorRating < 45),
        ),
        new Rating(
          RatingType.FourHalf,
          hotels.some(it => it.vendorRating >= 45),
        ),
      ];
    }

    static composeBase(): Rating[] {
      return [
        new Rating(RatingType.Any, true),
        new Rating(RatingType.ThreeHalf, true),
        new Rating(RatingType.Four, true),
        new Rating(RatingType.FourHalf, true),
      ];
    }
  }

  export class PriceRange {
    min: number;
    max: number;
    left: number;
    right: number;

    constructor(min: number, max: number, left: number, right: number) {
      this.min = min;
      this.max = max;
      this.left = left;
      this.right = right;
    }

    static compose(offer: CarDetails[]): PriceRange {
      const hotelsWithPrice = offer.filter(it => it.price);
      const minPrices: number[] = hotelsWithPrice
        .slice()
        .map(it => it.price.totalWithCashback);
      if (hotelsWithPrice.length === 0) return new PriceRange(0, 0, 0, 0);
      const min = minPrices.min();
      const max = minPrices.max();
      return new PriceRange(min, max, min, max);
    }

    applied(): boolean {
      return this.left != this.min || this.max != this.right;
    }
  }

  export class Search {
    cars: CarDetails[];
    filter: FilterOptions;
    searchedCriteria: string;
    searchFinishedTime: number = 0;

    constructor(
      searchResult: SearchResults,
      route: string,
      searchFinishedTime: number,
    ) {
      const pickUpDate = parse(searchResult.pick_up_date);
      const dropOffDate = parse(searchResult.drop_off_date);

      const pickUpTime = [
        searchResult.pick_up_time.substr(0, 2),
        searchResult.pick_up_time.substr(2),
      ];
      const dropOffTime = [
        searchResult.drop_off_time.substr(0, 2),
        searchResult.drop_off_time.substr(2),
      ];

      pickUpDate.setHours(parseInt(pickUpTime[0]));
      pickUpDate.setMinutes(parseInt(pickUpTime[1]));
      dropOffDate.setHours(parseInt(dropOffTime[0]));
      dropOffDate.setMinutes(parseInt(dropOffTime[1]));

      this.cars = searchResult.offers.map(it => {
        const vendor: Vendor = searchResult.vendors.find(
          a => a.code == it.vendor.code,
        );
        const policy = searchResult.cancellation_policy[it.cancellation_policy];
        const dropOffStation = searchResult.stations.find(
          a => a.station_id == it.dropoff_station_id,
        );
        const pickUpStation = searchResult.stations.find(
          a => a.station_id == it.pickup_station_id,
        );

        return new CarDetails({
          offer: it,
          pickUpStation: pickUpStation,
          dropOffStation: dropOffStation,
          cancellationPolicy: policy,
          vendor: vendor,
          dropOffDate: dropOffDate,
          pickUpDate: pickUpDate,
        });
      });

      this.filter = FilterOptions.compose(this.cars);
      this.searchedCriteria = route;
      this.searchFinishedTime = searchFinishedTime;
    }

    filtered(query: string): CarDetails[] {
      const filter = this.filter;

      const filtered = this.cars.filter(car => {
        return (
          filter.type.nothingSelected() ||
          (filter.type
            .filterSelected()
            .every(it => (it as CarType).matches(car)) &&
            filter.rating
              .filterSelected()
              .every(it => (it as Rating).matches(car)) &&
            (filter.transmissions.nothingSelected() ||
              filter.transmissions
                .filterSelected()
                .some(it => (it as Options).name == car.transmission)) &&
            (filter.vendors.nothingSelected() ||
              filter.vendors
                .filterSelected()
                .some(it => (it as Options).name == car.vendorName)) &&
            (filter.fuelType.nothingSelected() ||
              filter.fuelType
                .filterSelected()
                .some(it => (it as Options).name == car.fuelType)) &&
            (filter.seats.nothingSelected() ||
              filter.seats
                .filterSelected()
                .some(it => (it as Options).name == car.passangers)) &&
            ((filter.freeCancellation &&
              (!filter.freeCancellation.selected || car.freeCancellation)) ||
              true) &&
            ((filter.postPay &&
              (!filter.postPay.selected ||
                car.source.rate_type == PayType.PayLater)) ||
              true) &&
            this.priceFilterСondition(filter, car))
        );
      });

      return filtered.filter(
        it =>
          it.vendorName.toLowerCase().includes(query.toLocaleLowerCase()) ||
          it.carName.toLowerCase().includes(query.toLowerCase()),
      );
    }

    priceFilterСondition(filter: FilterOptions, car: CarDetails): boolean {
      const price = car.price.totalWithCashback;
      if (filter.priceRange.right === 0) return true;
      return (
        filter.priceRange.left <= price &&
        (filter.priceRange.right === filter.priceRange.max
          ? true
          : price <= filter.priceRange.right)
      );
    }

    resetFilters() {
      this.filter = FilterOptions.compose(this.cars);
    }
  }

  export class CarDetails {
    source: Offer;
    pickUpStation?: Station;
    dropOffStation?: Station;
    price: PriceDetails;
    carName: string;
    carImage: string;
    doorsCount: string;
    passangers: string;
    baggage: string;
    transmission: string;
    vendorRating?: number;
    vendorName: string;
    vendorLogo?: string;
    category: string;
    freeCancellation: boolean;
    fuelType?: string;
    days: number;
    pickUpDate: Date;
    dropOffDate: Date;
    cancellationPolicy?: string;
    extras: string[];
    importantInfo: ImportantInformation[];

    constructor({
      offer,
      pickUpStation,
      dropOffStation,
      dropOffDate,
      pickUpDate,
      cancellationPolicy,
      vendor,
    }: {
      offer: Offer;
      pickUpStation: Station;
      dropOffStation: Station;
      pickUpDate: Date;
      dropOffDate: Date;
      cancellationPolicy: string;
      vendor: Vendor;
    }) {
      const days = Math.ceil(
        Math.abs(dropOffDate.getTime() - pickUpDate.getTime()) /
          (1000 * 3600 * 24),
      );

      this.source = offer;
      this.pickUpStation = pickUpStation;
      this.dropOffStation = dropOffStation;
      this.price = new PriceDetails(offer.price);
      this.carName = offer.car.make_model;
      this.carImage = offer.car.images[0];
      this.doorsCount = offer.car.door_count;
      this.passangers = offer.car.passengers;
      this.baggage = offer.car.baggage_seat;
      this.transmission = offer.car.transmission;
      this.category = offer.car.category;
      this.vendorLogo = vendor.image ?? vendor.image_our;
      this.vendorRating = vendor.ratings ? vendor.ratings.average : 0;
      this.vendorName = vendor.name;
      this.freeCancellation = offer.free_cancellation;
      this.days = days;
      this.pickUpDate = pickUpDate;
      this.dropOffDate = dropOffDate;
      this.cancellationPolicy = cancellationPolicy;
      this.importantInfo = this.composeInfo(offer);

      const matchFuelTypeToTranslation = (fuelType: string): string => {
        fuelType = fuelType.toLowerCase();

        let key = 'any';

        const allowed = ['petrol', 'diesel', 'electric', 'hybrid'];

        if (allowed.includes(fuelType)) {
          key = fuelType;
        } else {
          key = 'any';
        }

        return String(i18n.global.t(`carsSearchpage.filters.fuelTypes.${key}`));
      };

      this.fuelType = matchFuelTypeToTranslation(offer.car.fuel_type);

      this.extras = offer.extras;
      if (offer.free_cancellation) {
        this.extras.push(String(i18n.global.t('filters.freeCancellation')));
      }
      if (offer.unlimited_mileage) {
        this.extras.push(
          String(i18n.global.t('carPolicy.mileage.unlimitedMileageFull')),
        );
      }
    }

    composeInfo(offer: Offer): ImportantInformation[] {
      const list: ImportantInformation[] = [];
      offer.excesses_info.forEach(it => {
        if (!it || !it.amount) return;

        const amount = it.amount.formatCurrency({
          currency: it.currency,
        });

        if (it.type == ExcessesInfoType.security) {
          list.push({
            title: String(
              i18n.global.t('carPolicy.security.title', { amount }),
            ),
            info: String(i18n.global.t('carPolicy.security.info', { amount })),
          });
        } else if (it.type == ExcessesInfoType.damage) {
          list.push({
            title: String(i18n.global.t('carPolicy.damage.title', { amount })),
            info: String(i18n.global.t('carPolicy.damage.info', { amount })),
          });
        }
      });

      const mileage = offer.unlimited_mileage
        ? String(i18n.global.t('carPolicy.mileage.unlimitedMileage'))
        : String(i18n.global.t('carPolicy.mileage.limitedMileage'));
      list.push({
        title: String(i18n.global.t('carPolicy.mileage.title')),
        info: mileage,
      });
      if (offer.fuel_policy) {
        list.push({
          title: String(
            i18n.global.t('carPolicy.fuel.title', {
              name: offer.fuel_policy.name,
            }),
          ),
          info: offer.fuel_policy.description,
        });
      }

      return list;
    }

    static offerFactory(response: OfferDetailsResponse): CarDetails {
      const policy = response.offer.cancellation_policy;
      const pickUpDate = parse(response.pickup_date).toUTC();
      const dropOffDate = parse(response.return_date).toUTC();
      return new Cars.CarDetails({
        offer: response.offer,
        dropOffStation: response.dropoff,
        pickUpStation: response.pickup,
        cancellationPolicy: policy,
        vendor: response.vendor,
        pickUpDate: pickUpDate,
        dropOffDate: dropOffDate,
      });
    }

    getFormattedFullDate(date: Date, lang?: Lang.LangItem): string {
      const locale = lang ? lang : Lang.DEFAULT_LANG();
      return date.toLocaleString(locale.locale, {
        weekday: 'long',
        month: 'long',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    getPriceDetails(included: boolean): PriceDetail[] {
      return this.source.price_details.filter(it => it.included == included);
    }
  }

  export interface ImportantInformation {
    title: string;
    info: string;
  }

  export class PriceDetails {
    source: Price;
    currency: string;
    cashback: number;
    daily: number;
    deposit: Deposit;
    publicLowest: number;
    publicLowestDaily: number;
    savings: number;
    total: number;
    totalWithCashback: number;

    constructor(price: Price) {
      this.source = price;
      this.currency = price.currency;
      this.cashback = price.cashback;
      this.daily = price.daily;
      this.deposit = price.deposit;
      this.publicLowest = price.public_lowest;
      this.savings = price.savings;
      this.total = price.total;
      this.totalWithCashback = price.total_with_cashback;
    }
  }
}
